import { Box, Icon, styled, Theme, Typography } from "@outschool/backpack";
import { faBellConcierge, faComment, faQuestionCircle } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { parentProfilePath, pathnameWithoutLocale } from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import { useImpressionTracking } from "@outschool/ui-legacy-component-library";
import { lazyWithRetry } from "@outschool/ui-utils";
import React from "react";
import { NavLink } from "react-router-dom";

import * as Routes from "../../../shared/Routes";
import * as User from "../../../shared/User";
import useConcierge from "../../hooks/useConcierge";
import { useCreditOffer } from "../../hooks/useCreditOffer";
import { useTeacherUnifiedMessagingM2 } from "../../hooks/useTeacherUnifiedMessaging";
import { UserMode } from "../../lib/UserMode";
import IconBadgeWithNumber from "../IconBadgeWithNumber";
import { ConciergeNavItem } from "./ConciergeNavItem";

const EsaMembershipNavItem = lazyWithRetry(() =>
  import("./EsaMembershipNavItem").then(module => ({
    default: module.EsaMembershipNavItem,
  })),
);
const SubscriptionCreditNavItem = lazyWithRetry(() =>
  import("./SubscriptionCreditNavItem").then(module => ({
    default: module.SubscriptionCreditNavItem,
  })),
);
const CreditOfferNavItem = lazyWithRetry(() =>
  import("./CreditOfferNavItem").then(module => ({
    default: module.CreditOfferNavItem,
  })),
);
const JoinSubscriptionNavItem = lazyWithRetry(() =>
  import("./JoinSubscriptionNavItem").then(module => ({
    default: module.JoinSubscriptionNavItem,
  })),
);

const StyledLink = styled(NavLink)(({ theme }) => {
  return {
    position: "relative",
    whiteSpace: "nowrap",
    width: "100%",
    textDecoration: "none !important",
    color: theme.palette.grey[500],
    paddingTop: 5,
    display: "flex",
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    border: 0,
    borderBottom: 4,
    borderStyle: "solid",
    borderColor: "white",
    "&:hover": {
      color: theme.palette.grey[900],
      border: 0,
      borderBottom: 4,
      borderStyle: "solid",
      borderColor: theme.palette.grey[400],
    },
    ["&[aria-current='page']"]: {
      color: theme.palette.primary.main,
      border: 0,
      borderBottom: 4,
      borderStyle: "solid",
      borderColor: theme.palette.primary.main,
    },
    "&:hover:not([aria-current='page'])": {
      color: theme.palette.grey[900],
    },
  };
});

const StyledIcon = styled(Icon)(({ theme }) => ({
  fontSize: "1.5em",
  ["[aria-current='page'] &"]: { color: theme.palette.primary.main },
  height: 28,
}));

export function DesktopNavTabs() {
  const { t } = useTranslation("client\\components\\nav\\DesktopHeaderNav");
  const { currentUser, isLeader } = useSession();
  const { creditOffer } = useCreditOffer();
  const isOnOwnProfile = currentUser
    ? // eslint-disable-next-line no-restricted-globals
      pathnameWithoutLocale(location.pathname).includes(
        "/parents/" + currentUser.uid,
      )
    : false;

  const hasActiveSubscription = currentUser?.hasSubscription;
  const hasEsaMembership = currentUser?.hasEsaMembership;
  const isTeacherUnifiedMessagingM2Enabled = useTeacherUnifiedMessagingM2();

  return (
    <Box flex sx={{ alignItems: "center", gap: 16 }}>
      {hasEsaMembership ? (
        <EsaMembershipNavItem />
      ) : hasActiveSubscription ? (
        <SubscriptionCreditNavItem />
      ) : creditOffer ? (
        <CreditOfferNavItem />
      ) : (
        <JoinSubscriptionNavItem />
      )}
      <ConciergeNavItem />
      <DesktopNavTab
        to={Routes.conversationsPath(
          isLeader ? UserMode.Educator : UserMode.Parent,
        )}
        trackingName={"messages"}
        icon={<StyledIcon icon={faComment} />}
      >
        {t("Messages")}
        <IconBadgeWithNumber
          color="red"
          count={User.unreadConversationsCount(
            currentUser,
            isLeader && isTeacherUnifiedMessagingM2Enabled,
          )}
          sx={{ position: "absolute", right: 0, top: 0 }}
          dataTestId={"unread-message-count"}
        />
      </DesktopNavTab>
      <DesktopNavTab
        isActive={(_, location) =>
          isOnOwnProfile ||
          pathnameWithoutLocale(location.pathname).includes("/account/parent")
        }
        to={parentProfilePath(currentUser?.uid || "")}
        trackingName={"profile"}
        icon={
          <Box
            className="amp-block"
            sx={(theme: Theme) => ({
              position: "relative",
              border: 2,
              borderColor: theme.palette.grey[500],
              borderRadius: "50%",
            })}
          >
            <UserHeadshotImage size={24} user={currentUser} />
            {isOnOwnProfile && (
              <Box
                sx={() => ({
                  position: "absolute",
                  width: 26,
                  height: 26,
                  border: `2px solid ${"primary.main"}`,
                  borderRadius: "50%",
                  top: -1,
                  left: -1,
                })}
              />
            )}
          </Box>
        }
      >
        {t("My profile")}
      </DesktopNavTab>
    </Box>
  );
}

function DesktopNavTab({
  icon,
  children,
  to,
  trackingName,
  onClick,
  ...props
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  trackingName: string;
  onClick?: () => void;
} & React.ComponentProps<typeof NavLink>) {
  const { trackTouch } = useImpressionTracking({
    trackingLabel: trackingName,
    uniqueId: `${trackingName}_mobile_footer_nav`,
    trackingEventName: `${trackingName}_mobile_footer_nav_event`,
  });

  return (
    <StyledLink
      to={to}
      {...props}
      onClick={() => {
        trackTouch();
        if (onClick) {
          onClick();
        }
      }}
    >
      {icon}
      <Typography
        variant="caption"
        sx={{
          // TODO: Update this to use normal caption font size
          fontSize: "1.2rem",
          letterSpacing: "0.2px",
          fontWeight: 400,
          paddingX: 4,
        }}
      >
        {children}
      </Typography>
    </StyledLink>
  );
}

export function ConciergeNavTab({ onClick }: { onClick?: () => void }) {
  const { t } = useTranslation("client\\components\\nav\\DesktopHeaderNav");
  const { trackTouch } = useImpressionTracking({
    trackingLabel: "concierge_nav",
    uniqueId: "concierge_nav_mobile_footer_nav",
    trackingEventName: "concierge_nav_mobile_footer_nav_event",
  });

  const { canAccessConcierge } = useConcierge();
  return (
    <StyledLink
      to="#"
      onClick={() => {
        trackTouch();

        if (onClick) {
          onClick();
        }
      }}
      isActive={() => false}
    >
      <StyledIcon
        icon={canAccessConcierge ? faBellConcierge : faQuestionCircle}
      />
      <Typography
        variant="caption"
        sx={{
          // TODO: Update this to use normal caption font size
          fontSize: "1.2rem",
          letterSpacing: "0.2px",
          fontWeight: 400,
          paddingX: 4,
        }}
      >
        {canAccessConcierge ? t("Concierge") : t("Help")}
      </Typography>
    </StyledLink>
  );
}
